import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import Cropper from "react-easy-crop";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import getCroppedImg from "./cropImage";
import {
  uploadNewUserImage,
  updateAssetPrimary,
} from "../../../store/actions/talents";
import { personTypeUUID } from "../../../constants"

const ProfilePicCropper = ({
  imageSrc,
  closeModal,
  setResizedImage,
  companyUUID,
  userId,
  triggerAddImage,
  triggerAssetPrimary,
  isTalentView = false,
  isTalentRegister = false,
  newTalentId
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [loading, setLoading] = useState(false);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = useCallback(async () => {
    if (!croppedAreaPixels) {
      toast.warn("Please adjust the crop area before saving.");
      return;
    }

    setLoading(true);

    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], "profile.jpg", {
        type: "image/jpeg",
      });

      const croppedUrl = URL.createObjectURL(croppedBlob);
      setResizedImage(croppedUrl);

      const personId = isTalentView ? userId : newTalentId;

      const actualPayload = {
        companyId: companyUUID,
        personId: personId,
        asset: croppedFile,
        isTalentView,
        isRegister: isTalentRegister,
        isCropped: true,
      };

      const uploadPayload = {
        actualPayload,
        onSuccess: (id) => {
          if (id) {
            const primaryPayload = {
              actualPayload: {
                uuid: personId,
                person_type_uuid: personTypeUUID,
                primary_photo_attribute_uuid: id,
              },
              onSuccess: () => {
                toast.info("Image cropped and uploaded successfully!");
              },
              onError: (err) => {
                console.error("Failed to set primary photo:", err);
                toast.error("Could not set as primary photo.");
              },
            };

            triggerAssetPrimary(primaryPayload);
          }
        },
        onError: (error) => {
          console.error("Upload failed:", error);
          toast.error("Image cropping failed. Please try again.");
        },
      };

      triggerAddImage(uploadPayload);
      closeModal();
    } catch (error) {
      console.error("Error while cropping:", error);
      toast.error("Something went wrong while cropping the image.");
    } finally {
      setLoading(false);
    }
  }, [
    croppedAreaPixels,
    imageSrc,
    closeModal,
    setResizedImage,
    companyUUID,
    userId,
    triggerAddImage,
    triggerAssetPrimary,
    isTalentView,
    isTalentRegister,
  ]);

  return (
    <dialog
      open
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85"
    >
      <div className="relative flex flex-col w-96 bg-white shadow-lg overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-xl font-semibold">Crop Profile Picture</h3>
          <button
            aria-label="Close modal"
            onClick={(e) => {
              e.stopPropagation();
              closeModal();
            }}
            className="p-2 rounded hover:bg-gray-100 focus:outline-none"
          >
            ✕
          </button>
        </div>

        <div className="relative w-60 h-60 m-auto mt-8 mb-4">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            showGrid={false}
          />
        </div>

        <div className="flex items-center justify-center gap-3 px-4 mb-6">
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 h-1 cursor-pointer accent-secondary rounded"
            aria-label="Zoom"
          />
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeModal();
            }}
            type="button"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-cadetBlue/50 bg-white shadow-button hover:bg-gray-500 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            type="button"
            className={`px-4 py-2 text-sm font-medium text-white rounded transition ${
              loading
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-secondaryLight hover:bg-secondary"
            }`}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </dialog>
  );
};

ProfilePicCropper.propTypes = {
  imageSrc: PropTypes.string.isRequired,
  closeModal: PropTypes.func.isRequired,
  setResizedImage: PropTypes.func.isRequired,
  companyUUID: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  triggerAddImage: PropTypes.func.isRequired,
  triggerAssetPrimary: PropTypes.func.isRequired,
  isTalentView: PropTypes.bool,
  isTalentRegister: PropTypes.bool,
  newTalentId: PropTypes.string,
};

const mapStateToProps = (state) => ({
  companyUUID: state.talent?.companyUUID,
  newTalentId: state.talent.newTalentId,
});

const mapDispatchToProps = {
  triggerAddImage: uploadNewUserImage,
  triggerAssetPrimary: updateAssetPrimary,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProfilePicCropper);
